import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  StyleSheet,
  TextInput,
  Pressable,
  LayoutAnimation,
} from "react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { Calendar, Ruler, Weight } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import { BasicInfoQuestionLayout } from "../components";
import { useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { MoonFieldInput, MoonFormField } from "@louise/moon-ui";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  formatDateTyping,
  isoToDisplay,
  parseTypedToIso,
  placeholderFor,
  useDateFormat,
  type DateFormat,
} from "@src/packages/utils/useDateFormat";

/**
 * Try the primary locale format first, then fall back to the other layout so
 * a French user typing DD/MM/YYYY still saves even if their preference is
 * accidentally set to MM/DD/YYYY (and vice versa). Only used at parse time —
 * the display format stays whatever the user prefers.
 */
function parseTypedForgiving(input: string, primary: DateFormat): string {
  const iso = parseTypedToIso(input, primary);
  if (iso) return iso;
  const fallbacks: DateFormat[] =
    primary === "DD/MM/YYYY"
      ? ["MM/DD/YYYY", "YYYY-MM-DD"]
      : primary === "MM/DD/YYYY"
        ? ["DD/MM/YYYY", "YYYY-MM-DD"]
        : ["DD/MM/YYYY", "MM/DD/YYYY"];
  for (const fmt of fallbacks) {
    const alt = parseTypedToIso(input, fmt);
    if (alt) return alt;
  }
  return "";
}

export function BasicInfoScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("BasicInfoScreen");
  const { state, updateState, next } = useBasicInfo("BasicInfo");
  const navigation = useNavigation();
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();
  const { dateFormat } = useDateFormat();

  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");
  const [heightUnit, setHeightUnit] = useState<"cm" | "ft">("cm");
  const [weight, setWeight] = useState(state.weight?.toString() || "");
  const [height, setHeight] = useState(state.height?.toString() || "");

  // Initialize DOB text from existing state using locale-aware display.
  const [dobText, setDobText] = useState(
    state.dateOfBirth ? isoToDisplay(state.dateOfBirth, dateFormat) : "",
  );

  // Update DOB, height and weight when state changes (e.g., loaded from database)
  useEffect(() => {
    if (state.dateOfBirth) {
      setDobText(isoToDisplay(state.dateOfBirth, dateFormat));
    }
  }, [state.dateOfBirth, dateFormat]);

  useEffect(() => {
    if (state.height != null) {
      setHeight(state.height.toString());
    }
  }, [state.height]);

  useEffect(() => {
    if (state.weight != null) {
      setWeight(state.weight.toString());
    }
  }, [state.weight]);

  const handleDateChange = useCallback(
    async (text: string) => {
      const formatted = formatDateTyping(text, dateFormat, dobText);
      setDobText(formatted);
      // As soon as a complete valid date is typed, persist it. Blur alone
      // isn't reliable on web — the user can hit "Next" without blurring.
      const iso = parseTypedForgiving(formatted, dateFormat);
      if (iso && iso !== state.dateOfBirth) {
        updateState({ dateOfBirth: iso });
        await updatePatient({ dateOfBirth: iso });
      }
    },
    [dateFormat, dobText, state.dateOfBirth, updateState, updatePatient],
  );

  // Save DOB on blur (defensive — handleDateChange already persists once the
  // date is complete, but blur catches paste / autofill paths too).
  const handleDobBlur = useCallback(async () => {
    const iso = parseTypedForgiving(dobText, dateFormat);
    if (!iso || iso === state.dateOfBirth) return;
    updateState({ dateOfBirth: iso });
    await updatePatient({ dateOfBirth: iso });
  }, [dobText, dateFormat, state.dateOfBirth, updateState, updatePatient]);

  // Save weight to API on blur (always store in kg)
  const handleWeightBlur = useCallback(async () => {
    if (!weight) {
      updateState({ weight: null });
      return;
    }
    const raw = parseInt(weight, 10);
    const weightInKg = weightUnit === "lb" ? Math.round(raw * 0.453592) : raw;
    updateState({ weight: weightInKg });
    await updatePatient({
      bodyData: {
        id: patient?.bodyData?.id,
        weight: weightInKg,
      },
    });
  }, [weight, weightUnit, updateState, updatePatient, patient?.bodyData?.id]);

  // Save height to API on blur (always store in cm)
  const handleHeightBlur = useCallback(async () => {
    if (!height) {
      updateState({ height: null });
      return;
    }
    let heightInCm: number;
    if (heightUnit === "ft") {
      // Parse feet'inches format (e.g. "5'10" or just "510" → 5ft 10in)
      const match = height.match(/^(\d+)'?\s*(\d*)$/);
      if (match) {
        const feet = parseInt(match[1]!, 10);
        const inches = match[2] ? parseInt(match[2], 10) : 0;
        heightInCm = Math.round((feet * 12 + inches) * 2.54);
      } else {
        return;
      }
    } else {
      heightInCm = parseInt(height, 10);
    }
    updateState({ height: heightInCm });
    await updatePatient({
      bodyData: {
        id: patient?.bodyData?.id,
        height: heightInCm,
      },
    });
  }, [height, heightUnit, updateState, updatePatient, patient?.bodyData?.id]);

  const handleSave = async () => {
    // Save DOB before moving on
    const iso = parseTypedForgiving(dobText, dateFormat);
    if (iso && iso !== state.dateOfBirth) {
      updateState({ dateOfBirth: iso });
      await updatePatient({ dateOfBirth: iso });
    }
    next();
  };

  return (
    <BasicInfoQuestionLayout
      route="BasicInfo"
      title={t("med_history.basic_info.basics.title")}
      subtitle={t("med_history.basic_info.basics.subtitle")}
      helpText={t("med_history.basic_info.help.basics")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={isPending}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.formContainer}>
        {/* Date of Birth — text input with MM/DD/YYYY auto-format */}
        <MoonFormField
          label={t("med_history.basic_info.basics.date_of_birth")}
          labelIcon={Calendar}
        >
          <MoonFieldInput
            paddingY={2}
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            <TextInput
              value={dobText}
              onChangeText={handleDateChange}
              onBlur={handleDobBlur}
              placeholder={placeholderFor(dateFormat)}
              keyboardType="number-pad"
              maxLength={10}
              inputMode="numeric"
              autoComplete="off"
              textContentType="none"
              placeholderTextColor={EMBER.colors.textFaint}
              style={{
                flex: 1,
                paddingVertical: 14,
                paddingHorizontal: 0,
                fontSize: 15,
                color: EMBER.colors.text,
              }}
            />
          </MoonFieldInput>
        </MoonFormField>

        {/* Weight Input */}
        <MoonFormField
          label={t("med_history.basic_info.basics.weight")}
          labelIcon={Weight}
        >
          <MoonFieldInput
            paddingY={2}
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            <TextInput
              value={weight}
              onChangeText={(text) => {
                setWeight(text.replace(/[^0-9]/g, ""));
              }}
              onBlur={handleWeightBlur}
              placeholder={t(
                "med_history.basic_info.basics.weight_placeholder",
              )}
              keyboardType="number-pad"
              placeholderTextColor={EMBER.colors.textFaint}
              style={{
                flex: 1,
                paddingVertical: 14,
                paddingRight: 10,
                fontSize: 15,
                color: EMBER.colors.text,
              }}
            />
            <View
              style={{
                flexDirection: "row",
                marginRight: 4,
                padding: 2,
                borderRadius: 8,
                backgroundColor: EMBER.colors.border,
              }}
            >
              {(["kg", "lb"] as const).map((unit) => (
                <Pressable
                  key={unit}
                  onPress={() => {
                    capturePress(pressLabel(unit), "weight_unit");
                    LayoutAnimation.configureNext(
                      LayoutAnimation.Presets.easeInEaseOut,
                    );
                    setWeightUnit(unit);
                  }}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 6,
                    backgroundColor:
                      weightUnit === unit
                        ? EMBER.colors.surface
                        : "transparent",
                    ...(weightUnit === unit && {
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.1,
                      shadowRadius: 2,
                    }),
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: weightUnit === unit ? "600" : "400",
                      color:
                        weightUnit === unit
                          ? EMBER.colors.text
                          : EMBER.colors.textMid,
                    }}
                  >
                    {unit}
                  </Text>
                </Pressable>
              ))}
            </View>
          </MoonFieldInput>
        </MoonFormField>

        {/* Height Input */}
        <MoonFormField
          label={t("med_history.basic_info.basics.height")}
          labelIcon={Ruler}
        >
          <MoonFieldInput
            paddingY={2}
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            <TextInput
              value={height}
              onChangeText={(text) => {
                if (heightUnit === "ft") {
                  setHeight(text.replace(/[^0-9']/g, ""));
                } else {
                  setHeight(text.replace(/[^0-9]/g, ""));
                }
              }}
              onBlur={handleHeightBlur}
              placeholder={
                heightUnit === "ft"
                  ? t("med_history.basic_info.basics.height_placeholder_ft")
                  : t("med_history.basic_info.basics.height_placeholder_cm")
              }
              keyboardType={heightUnit === "ft" ? "default" : "number-pad"}
              placeholderTextColor={EMBER.colors.textFaint}
              style={{
                flex: 1,
                paddingVertical: 14,
                paddingRight: 10,
                fontSize: 15,
                color: EMBER.colors.text,
              }}
            />
            <View
              style={{
                flexDirection: "row",
                marginRight: 4,
                padding: 2,
                borderRadius: 8,
                backgroundColor: EMBER.colors.border,
              }}
            >
              {(["cm", "ft"] as const).map((unit) => (
                <Pressable
                  key={unit}
                  onPress={() => {
                    capturePress(pressLabel(unit), "height_unit");
                    LayoutAnimation.configureNext(
                      LayoutAnimation.Presets.easeInEaseOut,
                    );
                    setHeightUnit(unit);
                  }}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    borderRadius: 6,
                    backgroundColor:
                      heightUnit === unit
                        ? EMBER.colors.surface
                        : "transparent",
                    ...(heightUnit === unit && {
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.1,
                      shadowRadius: 2,
                    }),
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: heightUnit === unit ? "600" : "400",
                      color:
                        heightUnit === unit
                          ? EMBER.colors.text
                          : EMBER.colors.textMid,
                    }}
                  >
                    {unit}
                  </Text>
                </Pressable>
              ))}
            </View>
          </MoonFieldInput>
        </MoonFormField>
      </View>
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    gap: 24,
  },
});

export default BasicInfoScreen;
